<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<template>
  <div
    ref="videoPlayer"
    class="video-player"
  >
    <div
      class="video-player__controls"
      :class="`video-player__controls--${controlsAlignment}`"
    >
      <button
        class="video-player__control"
        :class="`video-player__control--${iconColor}`"
        type="button"
        aria-hidden="true"
        @click.self="handleControlClick"
        @hover="handleControlHover(true)"
        @mouseover="handleControlHover(true)"
        @mouseout="handleControlHover(false)"
        @focusin="handleControlHover(true)"
        @focusout="handleControlHover(false)"
      >
        <component :is="icon" />
      </button>

      <button
        v-if="subtitles"
        class="video-player__subtitles video-player__control"
        :class="{ [$class('active')]: displaySubtitles }"
        type="button"
        @click="displaySubtitles = !displaySubtitles"
      >
        <span
          class="visually-hidden"
          v-text="subtitlesText"
        />

        <span
          class="text-other-label-s"
          aria-hidden="true"
          v-text="$string('accessibility.video.cc')"
        />
      </button>
      <div
        class="video-player__progress-container"
        tabindex="0"
        @mouseover="isProgressBarHovered = true"
        @mouseout="isProgressBarHovered = false"
        @mousemove="handleProgressBarHover"
        @click="handleProgressBarClick"
      >
        <progress
          class="video-player__progress-bar"
          :value="progress"
          max="100"
        />
        <span
          class="video-player__current-time"
          :style="{ left: `${progress}%` }"
        >{{ formattedTime }}</span>
        <span
          v-if="isProgressBarHovered"
          class="video-player__timer"
          :style="{ left: `${hoverProgress}%` }"
        >{{ hoverFormattedTime }}</span>
      </div>
    </div>

    <button
      v-if="useOverlayButton"
      class="video-player__overlay"
      tabindex="-1"
      type="button"
      @click="handleControlClick"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.video.play')"
      />

      <transition name="fade">
        <responsive-image
          v-if="displayOverlay && image.src"
          class="video-player__image"
          :alt="false"
          :image="image"
          :lazyload="lazyload"
          :min-max="minMax"
          :padding="false"
        />
      </transition>
    </button>

    <component
      :is="ctaUrl ? 'a' : 'span'"
      :href="ctaUrl ? ctaUrl : null"
    >
      <video
        ref="video"
        v-bind="attributes"
        class="video-player__video"
        :type="`video/${video.format}`"
        :loop="loop"
        :muted="muted"
        @canplay="state = 'ready'"
        @canplaythrough="state = 'ready'"
        @loadedmetadata="state = 'ready'"
        @pause="state = 'paused'"
        @play="state = 'playing'"
        @timeupdate="handleVideoTimeUpdate"
      >
        <source
          :src="video.url"
          :type="video.mimeType"
        >

        <track
          v-if="subtitles"
          kind="captions"
          :label="$variable('store.languageName')"
          :src="subtitles"
          :srclang="$variable('store.language')"
        >
      </video>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Lookbook video player functionality.
 *
 * @param {Object} [attributes] - Video element attributes.
 * @param {String} [controlsAlignment] - Position of play/pause controls,
 * accepts 'left', 'right' or 'center'.
 * @param {String} [ctaUrl] - Make video a link to another page.
 * @param {Boolean} [displayOverlay] - Display image overlay.
 * @param {Boolean} [isActive] - Slide containing video is active.
 * @param {Boolean} [isAutoplay] - Use autoplay automatically.
 * @param {Object} [image] - Shopify image object.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {String} [minMax] - Min and max size for srcset in `min-max` format.
 * @param {String} [subtitles] - Video subtitles/captions .vtt file URL.
 * @param {Boolean} [useOverlayButton] - Click vide to play/pause.
 * @param {Object} video - Formatted media video object.
 *
 * @emits videoControlHover - Emits on control hover events.
 */
import { mapGetters } from 'vuex'
import { setIntersectionObserver } from '~/helpers/general'

import ResponsiveImage from '~global/images/responsive-image'

import IconLoading from '~icons/general/loading.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'

export default {
  name: 'VideoPlayer',

  components: {
    IconLoading,
    IconPause,
    IconPlay,
    ResponsiveImage,
  },

  props: {
    attributes: {
      type: Object,
      default: () => ({
        controlslist: 'nofullscreen',
        crossorigin: 'anonymous',
        disablepictureinpicture: true,
        muted: true,
        playsinline: true,
      }),
    },
    controlsAlignment: {
      type: String,
      default: 'left',
    },
    ctaUrl: {
      type: [Boolean, String],
      default: false,
    },
    displayOverlay: {
      type: Boolean,
      default: true,
    },
    image: {
      type: Object,
      default: () => ({
        alt: '',
        height: 0,
        src: '',
        width: 0,
      }),
      required: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isAutoplay: {
      type: Boolean,
      default: false,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    minMax: {
      type: String,
      default: '200-1000',
      validator: (value) => {
        return value.match(/^\d+-\d+$/g)
      },
    },
    subtitles: {
      type: String,
      default: '',
    },
    useOverlayButton: {
      type: Boolean,
      default: true,
    },
    video: {
      type: Object,
      default: () => ({
        format: '',
        mimeType: '',
        url: '',
      }),
      required: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    iconColor: {
      type: String,
      default: 'black',
    },
  },

  emits: {
    'video-control-hover': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      isProgressBarHovered: false,
      displaySubtitles: false,
      overlayPaused: false,
      state: 'loading',
      time: 0,
      hoverProgress: 0,
      hoverTime: 0,
    }
  },

  computed: {
    hoverFormattedTime() {
      const minutes = Math.floor(this.hoverTime / 60)
      const seconds = Math.floor(this.hoverTime % 60)
        .toString()
        .padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    progress() {
      return (this.time / this.$refs.video?.duration) * 100
    },
    formattedTime() {
      const minutes = Math.floor(this.time / 60)
      const seconds = Math.floor(this.time % 60)
        .toString()
        .padStart(2, '0')
      return `${minutes}:${seconds}`
    },

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute control text based on state.
     * @returns {String}
     */
    controlText() {
      switch (this.state) {
        case 'loading':
          return this.$string('accessibility.video.loading')

        case 'playing':
          return this.$string('accessibility.video.pause')

        case 'paused':
        default:
          return this.$string('accessibility.video.play')
      }
    },

    /**
     * Compute icon based on video state.
     * @returns {String}
     */
    icon() {
      switch (this.state) {
        case 'loading':
          return 'icon-loading'

        case 'playing':
          return 'icon-pause'

        case 'paused':
        default:
          return 'icon-play'
      }
    },

    /**
     * Compute subtitles text.
     * @returns {String}
     */
    subtitlesText() {
      return this.displaySubtitles
        ? this.$string('accessibility.video.hide_subtitles')
        : this.$string('accessibility.video.show_subtitles')
    },
  },

  watch: {

    /**
     * Watch display subtitles to show track.
     */
    displaySubtitles() {
      if (!this.displaySubtitles) {
        this.$refs.video.textTracks[0].mode = 'hidden'
        return
      }

      this.$refs.video.textTracks[0].mode = 'showing'
    },

    /**
     * Watch active overlay to pause/resume (if already playing).
     */
    getActiveOverlay() {
      if (this.getActiveOverlay) {
        if (this.state === 'playing') {
          this.pauseVideo()
          this.overlayPaused = true
          return
        }

        this.overlayPaused = false
        return
      }

      /**
       * When overlay closes if video was previously playing resume it.
       */
      if (this.overlayPaused) {
        this.playVideo()
      }
    },

    /**
     * Watch active state to play video when slide containing it becomes active.
     * - Don't autoplay if prefers reduced motion.
     */
    isActive() {
      this.handleAutoplay()
    },
  },

  mounted() {

    /**
     * Reset any subtitles that browser has enabled by default.
     */
    if (!this.$refs.video.textTracks.length) {
      Object.keys(this.$refs.video.textTracks).forEach((_, index) => {
        this.$refs.video.textTracks[index].mode = 'hidden'
      })
    }

    /**
     * Set intersection observer so that handleAutoplay() only runs when video
     * is in view.
     */
    this.setVideoIntersectionObserver()
  },

  methods: {

    /**
     * Handle hover on the progerssion bar.
     */
    handleProgressBarHover(event) {
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const width = rect.width
      this.hoverProgress = (offsetX / width) * 100
      this.hoverTime = (this.hoverProgress / 100) * this.$refs.video.duration
      if (this.$refs.video.duration) {
        this.hoverTime = (this.hoverProgress / 100) * this.$refs.video.duration
      } else {
        this.hoverTime = 0
      }
    },

    /**
     * Handle click on the progerssion bar.
     */
    handleProgressBarClick(event) {
      event.stopPropagation()
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const width = rect.width
      const clickProgress = (offsetX / width) * 100
      const clickTime = (clickProgress / 100) * this.$refs.video.duration
      this.$refs.video.currentTime = clickTime
      this.$refs.video.play()
    },

    /**
     * Handle autoplay.
     */
    handleAutoplay() {
      if (
        (this.isActive || this.isAutoplay) &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.playVideo()
        return
      }

      /**
       * Prevents preview image not displaying & the play/pause icon being
       * stuck on the loading icon when user prefers reduced motion.
       * - Only run if prefers reduced motion to prevent DOMException errors.
       */
      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        this.state === 'loading'
      ) {
        this.$refs.video.load()
      }

      this.pauseVideo()
    },

    /**
     * Handle control and overlay click.
     */
    handleControlClick() {
      if (this.state === 'loading') {
        return
      }

      if (this.state === 'playing') {
        this.pauseVideo()
        return
      }

      this.playVideo()
    },

    /**
     * Handle mouseover event.
     * @param {Boolean} hovered - Whether the controls were hovered.
     */
    handleControlHover(hovered) {
      this.$emit('video-control-hover', hovered)
    },

    /**
     * Play video.
     */
    async playVideo() {

      /**
       * Addtional check that the video exists (useful when page changes
       * are handled via Vue, e.g. PLPs).
       */
      if (!this.$refs.video) {
        return
      }

      /**
       * Prevents following error:
       * DOMException: The play() request was interrupted by a call to pause()
       */
      if (this.state === 'playing' && !this.$refs.video.paused) {
        return
      }

      try {
        await this.$refs.video.play()

      } catch (error) {
        cnvs.ReportError('Failed to play video', error)
      }
    },

    /**
     * Pause video.
     */
    async pauseVideo() {
      if (!this.$refs.video) {
        return
      }

      try {
        await this.$refs.video.pause()

      } catch (error) {
        cnvs.ReportError('Failed to pause video', error)
      }
    },

    /**
     * Handle time update on video.
     */
    handleVideoTimeUpdate() {
      this.time = this.$refs.video?.currentTime || 0
      this.state = 'playing'
    },

    /**
     * If the content card has a video selected to render, set an intersection
     * observer to handle play/pausing it when not in view.
     */
    setVideoIntersectionObserver() {
      if (!this.$refs.videoPlayer) {
        return
      }

      this.$nextTick().then(() => {
        const element = this.$refs.videoPlayer

        this.observer = setIntersectionObserver({
          callback: this.handleComponentIntersection,
          element,
          rootMargin: '0px',
          threshold: 0.1,
        })
      })
    },

    /**
     * Handle component intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleComponentIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.pauseVideo()
          return
        }

        this.handleAutoplay()
      })
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
